// src/views/incidencias/IncidenciasCrear.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FiTool,
  FiTrash2,
  FiCamera,
  FiUpload,
  FiX,
  FiMapPin,
  FiCalendar,
  FiBriefcase,
  FiUser,
  FiAlertTriangle,
  FiCheckCircle,
  FiInfo,
} from "react-icons/fi";
import axios from "axios";
import { API_BASE } from "../../apiConfig.js";
import { useToast } from "../../components/NeoToast";
import { useNavigate } from "react-router-dom";

// ✅ NUEVO: catálogo Municipio -> Oficinas
import { useInventarioCatalogos } from "../../hooks/useInventarioCatalogos";

export default function IncidenciasCrear() {
  const { showToast } = useToast();
  const navigate = useNavigate();

  // INCIDENCIAS_CREAR_CATALOGOS_DINAMICOS_V1
  const {
    municipios: catalogoMunicipios,
    getOficinasByMunicipioNombre,
  } = useInventarioCatalogos();

  // ===============================
  // Firma
  // ===============================
  const sigRef = useRef(null);

  // ===============================
  // Cámara PRO (getUserMedia)
  // ===============================
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  // ===============================
  // Evidencias (picker / cámara fallback)
  // ===============================
  const filePickerRef = useRef(null);
  const cameraPickerRef = useRef(null);
  const [evidencias, setEvidencias] = useState([]); // [{ id, dataUrl, name }]

  const [form, setForm] = useState({
    lugarReporte: "",
    fechaReporte: new Date().toISOString().slice(0, 10),
    cargo: "SOPORTE TÉCNICO",

    tipoIncidencia: "TÉCNICA",
    reportadoPor: "Administrador",
    nivelRiesgo: "CRITICO",
    descripcion: "",
    intervino: "",

    evidenciaTexto: "",

    nombreReportante: "",
  });

  // ===============================
  // ✅ NUEVO: Municipio / Oficina (front)
  // ===============================
  const municipios = useMemo(() => {
    return [...(catalogoMunicipios || [])]
      .map((municipio) => municipio.nombre)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, "es"));
  }, [catalogoMunicipios]);

  const [municipioSel, setMunicipioSel] = useState("");
  const [oficinaSel, setOficinaSel] = useState("");

  const oficinasDelMunicipio = useMemo(() => {
    if (!municipioSel) return [];

    return (getOficinasByMunicipioNombre(municipioSel) || [])
      .map((oficina) => oficina.nombre)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, "es"));
  }, [municipioSel, getOficinasByMunicipioNombre]);

  // Cuando elijas municipio/oficina, llena lugarReporte (sin quitar el input manual)
  useEffect(() => {
    if (municipioSel && oficinaSel) {
      setForm((f) => ({
        ...f,
        lugarReporte: `${municipioSel} - ${oficinaSel}`,
      }));
    }
  }, [municipioSel, oficinaSel]);

  // ===============================
  // PASTE (Ctrl+V) para capturas
  // ===============================
  useEffect(() => {
    const onPaste = async (e) => {
      if (!e.clipboardData?.items?.length) return;

      const items = Array.from(e.clipboardData.items);
      const imgItem = items.find((it) => it.type && it.type.startsWith("image/"));
      if (!imgItem) return;

      const file = imgItem.getAsFile();
      if (!file) return;

      const dataUrl = await fileToDataUrl(file);
      addEvidence(dataUrl, file.name || `captura-${Date.now()}.png`);
      showToast("Captura pegada en Evidencia adjunta.", "success");
    };

    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===============================
  // Helpers Evidencias
  // ===============================
  const addEvidence = (dataUrl, name = "evidencia.png") => {
    if (!dataUrl) return;

    setEvidencias((prev) => {
      if (prev.length >= 8) {
        showToast("Máximo 8 evidencias por incidencia.", "warning");
        return prev;
      }
      if (prev.some((p) => p.dataUrl === dataUrl)) return prev;
      return [...prev, { id: cryptoId(), dataUrl, name }];
    });
  };

  const removeEvidence = (id) => {
    setEvidencias((prev) => prev.filter((x) => x.id !== id));
  };

  // ===============================
  // DRAG & DROP
  // ===============================
  const onDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const files = Array.from(e.dataTransfer.files || []).filter((f) =>
      f.type.startsWith("image/")
    );
    if (!files.length) return;

    for (const f of files) {
      const dataUrl = await fileToDataUrl(f);
      addEvidence(dataUrl, f.name);
    }

    showToast("Evidencia(s) agregada(s).", "success");
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onPickFiles = async (files) => {
    const arr = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
    if (!arr.length) return;

    for (const f of arr) {
      const dataUrl = await fileToDataUrl(f);
      addEvidence(dataUrl, f.name);
    }
    showToast("Evidencia(s) agregada(s).", "success");
  };

  // ===============================
  // Cámara PRO + fallback a input capture
  // ===============================
  const openCamera = async () => {
    const fallbackToInput = () => {
      try {
        cameraPickerRef.current?.click();
      } catch {}
    };

    if (!navigator.mediaDevices?.getUserMedia) {
      showToast("Tu navegador no soporta cámara directa. Abriendo alternativa.", "warning");
      fallbackToInput();
      return;
    }

    try {
      setCameraLoading(true);

      // Intentos: trasera -> frontal -> cualquiera
      const tryConstraints = [
        { video: { facingMode: { exact: "environment" } }, audio: false },
        { video: { facingMode: { ideal: "environment" } }, audio: false },
        { video: { facingMode: { ideal: "user" } }, audio: false },
        { video: true, audio: false },
      ];

      let stream = null;
      let lastErr = null;

      for (const c of tryConstraints) {
        try {
          stream = await navigator.mediaDevices.getUserMedia(c);
          break;
        } catch (e) {
          lastErr = e;
        }
      }

      if (!stream) {
        console.warn("getUserMedia falló:", lastErr);
        showToast("No se pudo abrir la cámara directa. Abriendo alternativa.", "warning");
        fallbackToInput();
        return;
      }

      // abre modal primero
      streamRef.current = stream;
      setCameraOpen(true);

      // engancha stream al video
      setTimeout(() => {
        const video = videoRef.current;
        if (!video) return;

        video.setAttribute("playsinline", "true");
        video.muted = true;
        video.autoplay = true;
        video.srcObject = stream;

        video.onloadedmetadata = async () => {
          try {
            await video.play();
          } catch (err) {
            console.warn("video.play() bloqueado:", err);
            showToast("Tu navegador bloqueó la reproducción. Usa la alternativa.", "warning");
            closeCamera();
            fallbackToInput();
          }
        };
      }, 50);
    } catch (e) {
      console.error(e);
      showToast("No se pudo abrir la cámara. Abriendo alternativa.", "warning");
      try {
        cameraPickerRef.current?.click();
      } catch {}
    } finally {
      setCameraLoading(false);
    }
  };

  const closeCamera = () => {
    setCameraOpen(false);

    const video = videoRef.current;
    if (video) {
      try {
        video.pause();
        video.srcObject = null;
      } catch {}
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const takePhoto = async () => {
    try {
      const video = videoRef.current;
      if (!video) return;

      const canvas = document.createElement("canvas");
      const w = video.videoWidth || 1280;
      const h = video.videoHeight || 720;

      canvas.width = w;
      canvas.height = h;

      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, w, h);

      // JPEG para que pese menos
      const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
      addEvidence(dataUrl, `camara-${Date.now()}.jpg`);

      showToast("Foto capturada y agregada a evidencia.", "success");
      closeCamera();
    } catch (e) {
      console.error(e);
      showToast("No se pudo capturar la foto.", "error");
    }
  };

  // ===============================
  // ENVIAR
  // ===============================
 const enviar = async () => {
  // ✅ Derivar municipio/oficina de selects o del texto "Municipio - Oficina"
  const parseLugar = (txt = "") => {
    const s = String(txt).trim();
    // acepta "Municipio - Oficina" o "Municipio | Oficina"
    const m = s.match(/^(.+?)\s*[-|]\s*(.+)$/);
    if (!m) return { municipio: "", oficina: "" };
    return { municipio: m[1].trim(), oficina: m[2].trim() };
  };

  const fromLugar = parseLugar(form.lugarReporte);
  const finalMunicipio = (municipioSel || fromLugar.municipio || "").trim();
  const finalOficina = (oficinaSel || fromLugar.oficina || "").trim();

  // ✅ Si quieres obligar oficina cuando hay municipio:
  if (!finalMunicipio) return showToast("Selecciona un municipio.", "warning");
  if (!finalOficina) return showToast("Selecciona una oficina.", "warning");

  // ✅ Mantén tu validación actual
  if (!form.lugarReporte.trim())
    return showToast("Ingresa el lugar del reporte.", "warning");
  if (!form.cargo.trim()) return showToast("Ingresa el cargo.", "warning");
  if (!form.descripcion.trim())
    return showToast("Ingresa la descripción de la incidencia.", "warning");
  if (!form.nombreReportante.trim())
    return showToast("Ingresa el nombre del reportante.", "warning");

  const firmaDataUrl = sigRef.current?.getDataUrl();
  if (!firmaDataUrl) return showToast("Falta la firma del reportante.", "warning");

  try {
   const payload = {
  ...form,
  fechaReporte: new Date(form.fechaReporte).toISOString(),
  evidencias: evidencias.map((e) => ({ name: e.name, dataUrl: e.dataUrl })),
  firmaDataUrl,
  municipio: finalMunicipio,
  oficina: finalOficina,

  // ✅ UNIFICADO: lo que escribe en Nombre del reportante
  // se manda también como "reportadoPor"
  reportadoPor: form.nombreReportante,
};

    const res = await axios.post(`${API_BASE}/incidencias`, payload);

    showToast("Incidencia registrada correctamente.", "success");
    if (res.data?.id) navigate(`/incidencias/${res.data.id}`);
  } catch (error) {
    console.error("ERROR GUARDAR INCIDENCIA:", error);
    showToast("Error al registrar la incidencia.", "error");
  }
};

  // ===============================
  // UI HELPERS
  // ===============================
  const errorLugar = !form.lugarReporte.trim();
  const errorCargo = !form.cargo.trim();
  const errorDesc = !form.descripcion.trim();
  const errorNombre = !form.nombreReportante.trim();
  const missingCount = [errorLugar, errorCargo, errorDesc, errorNombre].filter(Boolean).length;

  const setTipo = (val) => setForm((f) => ({ ...f, tipoIncidencia: val }));
  const setRiesgo = (val) => setForm((f) => ({ ...f, nivelRiesgo: val }));

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-28">
      {/* HEADER */}
      <div className="flex items-start justify-between gap-4 pt-6 pb-4">
        <div className="flex items-center gap-3">
          
          <div>
            <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 tracking-tight">
              Crear Incidencia
            </h2>
            
          </div>
        </div>

        <div className="hidden sm:flex items-center gap-2 text-xs text-slate-500">
         
      
        </div>
      </div>

      {/* CARD PRINCIPAL */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
        {/* BARRA DE ESTADO */}
        <div className="px-5 sm:px-7 py-4 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2 text-sm">
            
            <span className="font-semibold text-slate-800">Formulario de incidencia</span>
            <span className="text-slate-500">•</span>
            <span className="text-slate-500">{evidencias.length}/8 evidencias</span>
          </div>

          <div className="flex items-center gap-2 text-xs">
            {missingCount === 0 ? (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                <FiCheckCircle /> Listo para registrar
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                <FiInfo /> Faltan {missingCount} campo(s) obligatorios
              </span>
            )}
          </div>
        </div>

        <div className="p-5 sm:p-7">
          {/* GRID RESPONSIVE */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* LEFT */}
            <div className="lg:col-span-7 space-y-6">
              {/* Lugar */}
              <Field label="Lugar del reporte *" error={errorLugar ? "Obligatorio" : ""}>
                {/* ✅ NUEVO: Municipio / Oficina */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="text-xs font-bold text-slate-600 block mb-1">Municipio</label>
                    <select
                      className={inputCls(false)}
                      value={municipioSel}
                      onChange={(e) => {
                        const next = e.target.value;
                        setMunicipioSel(next);
                        setOficinaSel(""); // reset oficina al cambiar municipio
                      }}
                    >
                      <option value="">Selecciona municipio...</option>
                      {municipios.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-xs font-bold text-slate-600 block mb-1">Oficina</label>
                    <select
                      className={inputCls(false)}
                      value={oficinaSel}
                      onChange={(e) => setOficinaSel(e.target.value)}
                      disabled={!municipioSel}
                      title={!municipioSel ? "Selecciona primero un municipio" : ""}
                    >
                      <option value="">
                        {!municipioSel ? "Selecciona municipio primero..." : "Selecciona oficina..."}
                      </option>
                      {oficinasDelMunicipio.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

               

                {(municipioSel || oficinaSel) && (
                  <div className="mt-2 text-xs text-slate-500">
                    Tip: si quieres volver a escribir manual, edita el campo de arriba.
                  </div>
                )}
              </Field>

              {/* Descripción */}
              <Field label="Descripción de la incidencia *" error={errorDesc ? "Obligatorio" : ""}>
                <textarea
                  rows="5"
                  className={textareaCls(errorDesc)}
                  value={form.descripcion}
                  onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                  placeholder="Qué pasó / cuándo / impacto / qué se intentó..."
                />
                
              </Field>

              {/* Intervino */}
              <Field label="Persona(s) que intervinieron (opcional)">
                <input
                  className={inputCls(false)}
                  value={form.intervino}
                  onChange={(e) => setForm({ ...form, intervino: e.target.value })}
                  placeholder="Ej. Juan Pérez, María López"
                />
              </Field>

              {/* Evidencia */}
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <label className="text-sm font-semibold text-slate-800">Evidencia adjunta (opcional)</label>
                  <span className="text-xs text-slate-500">{evidencias.length}/8</span>
                </div>

                <textarea
                  rows="2"
                  className={textareaCls(false)}
                  placeholder="Describe la evidencia (opcional). También puedes pegar capturas con Ctrl+V."
                  value={form.evidenciaTexto}
                  onChange={(e) => setForm({ ...form, evidenciaTexto: e.target.value })}
                />

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => filePickerRef.current?.click()}
                    className="btnSoft"
                  >
                    <FiUpload />
                    Adjuntar imágenes
                  </button>

                  <button
                    type="button"
                    onClick={openCamera}
                    className="btnSoft"
                    disabled={cameraLoading}
                  >
                    <FiCamera />
                    {cameraLoading ? "Abriendo..." : "Abrir cámara"}
                  </button>

                  
                  <input
                    ref={filePickerRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => onPickFiles(e.target.files)}
                  />

                  {/* Fallback móvil: abre cámara/galería */}
                  <input
                    ref={cameraPickerRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => onPickFiles(e.target.files)}
                  />
                </div>

                <div
                  onDrop={onDrop}
                  onDragOver={onDragOver}
                  className="border-2 border-dashed border-slate-200 rounded-2xl p-4 bg-slate-50"
                >
                  {evidencias.length === 0 ? (
                    <div className="text-sm text-slate-600">
                      Arrastra aquí tus imágenes o pega una captura (Ctrl+V).
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                      {evidencias.map((ev) => (
                        <div
                          key={ev.id}
                          className="relative bg-white border border-slate-200 rounded-2xl overflow-hidden"
                        >
                          <img src={ev.dataUrl} alt={ev.name} className="w-full h-24 object-cover" />
                          <div className="px-2 py-2 text-xs text-slate-600 truncate">
                            {ev.name}
                          </div>

                          <button
                            type="button"
                            onClick={() => removeEvidence(ev.id)}
                            className="absolute top-2 right-2 bg-black/70 hover:bg-black text-white rounded-full p-1.5"
                            title="Quitar"
                          >
                            <FiX />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Firma */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-semibold text-slate-800">
                    Firma del reportante (dedo / mouse) *
                  </label>

                  <button
                    type="button"
                    onClick={() => sigRef.current?.clear()}
                    className="text-slate-600 hover:text-slate-900 flex items-center gap-2 text-sm"
                  >
                    <FiTrash2 />
                    Limpiar
                  </button>
                </div>

                <SignaturePad ref={sigRef} />
                <p className="text-xs text-slate-500 mt-2">
                  Firma dentro del recuadro. Se guardará en el PDF.
                </p>
              </div>
            </div>

            {/* RIGHT */}
            <div className="lg:col-span-5 space-y-6">
              {/* Datos del reporte */}
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 sm:p-5">
                <h3 className="text-sm font-bold text-slate-900 mb-4">Datos del reporte</h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
                  {/* Fecha */}
                  <FieldSmall label="Fecha de reporte" icon={<FiCalendar />}>
                    <input
                      type="date"
                      className={inputCls(false)}
                      value={form.fechaReporte}
                      onChange={(e) => setForm({ ...form, fechaReporte: e.target.value })}
                    />
                  </FieldSmall>

                  {/* Cargo */}
                  <FieldSmall label="Cargo *" icon={<FiBriefcase />} error={errorCargo ? "Obligatorio" : ""}>
                    <input
                      className={inputCls(errorCargo)}
                      value={form.cargo}
                      onChange={(e) => setForm({ ...form, cargo: e.target.value })}
                    />
                  </FieldSmall>

            
                </div>

                {/* Tipo: CHIPS */}
                <div className="mt-5">
                  <label className="text-sm font-semibold text-slate-800 block mb-2">
                    Tipo de incidencia
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {["TÉCNICA", "OPERATIVA", "SEGURIDAD", "SOFTWARE", "HARDWARE", "OTRA"].map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setTipo(t)}
                        className={`chip ${form.tipoIncidencia === t ? "chipOn" : ""}`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Riesgo: CHIPS */}
                <div className="mt-4">
                  <label className="text-sm font-semibold text-slate-800 block mb-2">
                    Nivel de riesgo
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { v: "CRITICO", label: "CRÍTICO" },
                      { v: "MEDIO", label: "MEDIO" },
                      { v: "BAJO", label: "BAJO" },
                    ].map((r) => (
                      <button
                        key={r.v}
                        type="button"
                        onClick={() => setRiesgo(r.v)}
                        className={`chip ${form.nivelRiesgo === r.v ? "chipOn" : ""}`}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-4 flex items-center gap-2 text-xs text-slate-600">
                  <FiCheckCircle className="text-emerald-600" />
                  
                </div>
              </div>

              {/* Nombre reportante */}
              <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
                <h3 className="text-sm font-bold text-slate-900 mb-4">Identificación</h3>

                <Field label="Nombre del reportante *" error={errorNombre ? "Obligatorio" : ""}>
                  <input
                    className={inputCls(errorNombre)}
                    placeholder="Ej. Juan Pérez"
                    value={form.nombreReportante}
                    onChange={(e) => setForm({ ...form, nombreReportante: e.target.value })}
                  />
                </Field>

                
              </div>
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="border-t border-slate-200 px-5 sm:px-7 py-4 flex items-center justify-between gap-3">
          <div className="hidden sm:flex items-center gap-2 text-xs text-slate-500">
            
            
          </div>

          <button onClick={enviar} className="btnPrimary">
            Registrar Incidencia
          </button>
        </div>
      </div>

      {/* Sticky botón en móvil */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 p-3 bg-white border-t border-slate-200">
        <button onClick={enviar} className="w-full btnPrimary">
          Registrar Incidencia
        </button>
      </div>

      {/* ===============================
          MODAL CÁMARA PRO
      =============================== */}
      {cameraOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-xl overflow-hidden shadow-xl">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div className="font-semibold text-slate-800 flex items-center gap-2">
                <FiCamera /> Cámara
              </div>
              <button
                type="button"
                onClick={closeCamera}
                className="px-3 py-1 rounded-lg border hover:bg-slate-50"
              >
                Cerrar
              </button>
            </div>

            <div className="p-4">
              <div className="rounded-xl overflow-hidden border bg-black">
                <video
                  ref={videoRef}
                  className="w-full h-[320px] object-cover"
                  playsInline
                  muted
                  autoPlay
                />
              </div>

              <div className="flex justify-end gap-3 mt-4">
                <button
                  type="button"
                  onClick={closeCamera}
                  className="px-5 py-2 rounded-xl border hover:bg-slate-50"
                >
                  Cancelar
                </button>

                <button
                  type="button"
                  onClick={takePhoto}
                  className="px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  Tomar foto
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSS inline (no rompe Tailwind, solo complementa) */}
      <style>{`
        .btnPrimary{
          background:#4f46e5;
          color:white;
          padding:12px 18px;
          border-radius:14px;
          font-weight:800;
          box-shadow: 0 10px 22px rgba(79,70,229,.18);
          transition: all .15s ease;
        }
        .btnPrimary:hover{ background:#4338ca; transform: translateY(-1px); }
        .btnSoft{
          display:flex;
          align-items:center;
          gap:8px;
          padding:9px 12px;
          border-radius:12px;
          border:1px solid #e2e8f0;
          background:white;
          color:#0f172a;
          font-weight:700;
          font-size:13px;
        }
        .btnSoft:hover{ background:#f8fafc; }
        .chip{
          padding:8px 10px;
          border-radius:999px;
          border:1px solid #e2e8f0;
          background:#fff;
          color:#0f172a;
          font-size:12px;
          font-weight:800;
        }
        .chip:hover{ background:#f8fafc; }
        .chipOn{
          border-color:#c7d2fe;
          background:#eef2ff;
          color:#3730a3;
        }
      `}</style>
    </div>
  );
}

/* =========================
   UI COMPONENTS
========================= */
function Field({ label, icon, error, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          {icon ? <span className="text-slate-500">{icon}</span> : null}
          {label}
        </label>
        {error ? <span className="text-xs text-red-600">{error}</span> : null}
      </div>
      {children}
    </div>
  );
}

function FieldSmall({ label, icon, error, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-xs font-bold text-slate-600 flex items-center gap-2">
          {icon ? <span className="text-slate-500">{icon}</span> : null}
          {label}
        </label>
        {error ? <span className="text-xs text-red-600">{error}</span> : null}
      </div>
      {children}
    </div>
  );
}

function inputCls(err) {
  return `w-full bg-white border ${err ? "border-red-300" : "border-slate-300"} rounded-xl px-4 py-3
  focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition-all`;
}
function textareaCls(err) {
  return `w-full bg-white border ${err ? "border-red-300" : "border-slate-300"} rounded-xl px-4 py-3
  focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition-all`;
}

/* ===========================================
   SignaturePad sin dependencias (mouse/touch)
=========================================== */
/* ===========================================
   SignaturePad ULTRA (all browsers)
   - Pointer Events (mouse/touch/pen)
   - touch-action: none
   - listeners NO pasivos en iOS
   - bloqueo temporal de scroll del body mientras se firma
=========================================== */
const SignaturePad = React.forwardRef(function SignaturePad(_, ref) {
  const canvasRef = React.useRef(null);
  const drawingRef = React.useRef(false);
  const lastRef = React.useRef({ x: 0, y: 0 });

  // Guardar estilos previos del body para restaurar
  const prevBodyRef = React.useRef(null);

  const lockBodyScroll = () => {
    if (prevBodyRef.current) return; // ya bloqueado
    const body = document.body;
    prevBodyRef.current = {
      overflow: body.style.overflow,
      overscrollBehavior: body.style.overscrollBehavior,
      touchAction: body.style.touchAction,
    };
    // bloquea scroll y gestos del body durante la firma
    body.style.overflow = "hidden";
    body.style.overscrollBehavior = "none";
    body.style.touchAction = "none";
  };

  const unlockBodyScroll = () => {
    const prev = prevBodyRef.current;
    if (!prev) return;
    const body = document.body;
    body.style.overflow = prev.overflow || "";
    body.style.overscrollBehavior = prev.overscrollBehavior || "";
    body.style.touchAction = prev.touchAction || "";
    prevBodyRef.current = null;
  };

  const resize = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const rect = canvas.getBoundingClientRect();

    // guarda firma
    const old = canvas.toDataURL("image/png");

    canvas.width = Math.floor(rect.width * ratio);
    canvas.height = Math.floor(rect.height * ratio);

    const ctx = canvas.getContext("2d");
    // Escala a pixeles CSS
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);

    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#111827";
    ctx.imageSmoothingEnabled = true;

    // restaurar firma previa
    if (old && old !== "data:,") {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
      img.src = old;
    }
  };

  React.useEffect(() => {
    resize();
    window.addEventListener("resize", resize, { passive: true });

    const canvas = canvasRef.current;
    if (!canvas) return () => {};

    // iOS Safari: se necesita listener NO pasivo para permitir preventDefault
    const prevent = (e) => {
      if (drawingRef.current) e.preventDefault();
    };

    // Bloquea scroll/zoom cuando el dedo está en canvas
    canvas.addEventListener("touchstart", prevent, { passive: false });
    canvas.addEventListener("touchmove", prevent, { passive: false });
    canvas.addEventListener("gesturestart", prevent, { passive: false }); // Safari viejo
    canvas.addEventListener("gesturechange", prevent, { passive: false });
    canvas.addEventListener("gestureend", prevent, { passive: false });

    return () => {
      window.removeEventListener("resize", resize);
      canvas.removeEventListener("touchstart", prevent);
      canvas.removeEventListener("touchmove", prevent);
      canvas.removeEventListener("gesturestart", prevent);
      canvas.removeEventListener("gesturechange", prevent);
      canvas.removeEventListener("gestureend", prevent);
      unlockBodyScroll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();

    // PointerEvent preferido; fallback touch
    const clientX = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY ?? 0;

    // Coordenadas en px CSS (no device pixels)
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const start = (e) => {
    // IMPORTANT: evitar scroll/zoom
    e.preventDefault();
    drawingRef.current = true;
    lockBodyScroll();

    const pos = getPos(e);
    lastRef.current = pos;

    // Capturar el puntero para no perder trazos si se sale del canvas
    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch {}
  };

  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const pos = getPos(e);

    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();

    lastRef.current = pos;
  };

  const end = (e) => {
    e.preventDefault();
    drawingRef.current = false;
    unlockBodyScroll();

    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    } catch {}
  };

  React.useImperativeHandle(ref, () => ({
    clear: () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      const rect = canvas.getBoundingClientRect();
      ctx.clearRect(0, 0, rect.width, rect.height);
    },
    getDataUrl: () => {
      const canvas = canvasRef.current;
      if (!canvas) return "";
      const data = canvas.toDataURL("image/png");
      return data && data !== "data:," ? data : "";
    },
  }));

  return (
    <div className="w-full">
      <canvas
        ref={canvasRef}
        className="w-full h-40 bg-white border border-slate-300 rounded-2xl"
        // CLAVE: sin pan/zoom en todos los browsers modernos
        style={{
          touchAction: "none",            // Edge/Chrome/Firefox/Samsung
          msTouchAction: "none",          // IE/Edge viejo (por si)
          WebkitUserSelect: "none",
          userSelect: "none",
          WebkitTapHighlightColor: "transparent",
        }}
        // Pointer Events (unifica mouse/touch/pen)
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
        onPointerLeave={end}
        // Fallback explícito por si algún browser no dispara pointer (raro)
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
      />
    </div>
  );
});


/* ===========================================
   Utils
=========================================== */
function cryptoId() {
  try {
    return crypto.randomUUID();
  } catch {
    return `id-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => resolve(String(reader.result || ""));
    reader.readAsDataURL(file);
  });
}
